import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import type { TeamMember, PersonDemand, PersonAvailability } from "@/types/roster";

export interface RosterMemberPayload {
  name: string;
  role: string;
  role_key: string;
  team?: string | null;
  vendor?: string | null;
  classification?: string | null;
  rate_per_hour: number;
  weekly_hrs_available: number;
  support_reserve_pct: number;
  include_in_capacity: boolean;
}

export function useRoster() {
  return useQuery<TeamMember[]>({
    queryKey: ["roster"],
    queryFn: () => api.get("/roster/").then((r) => r.data),
  });
}

/** When `includeApproved=true`, the backend extends the calculation to
 *  approved + ready_for_development projects. Useful for "what does the
 *  team look like 2 months from now when this approved work kicks in?"
 *  forecast views. Default false → headline number reflects current load. */
export function usePersonDemand(includeApproved = false) {
  return useQuery<PersonDemand[]>({
    queryKey: ["roster", "demand", { includeApproved }],
    queryFn: () =>
      api
        .get("/roster/demand", { params: { include_approved: includeApproved } })
        .then((r) => r.data),
  });
}

export function usePersonAvailability(threshold = 0.5) {
  return useQuery<PersonAvailability[]>({
    queryKey: ["roster", "availability", threshold],
    queryFn: () =>
      api.get("/roster/availability", { params: { threshold } }).then((r) => r.data),
  });
}

/** One project a person is committed to (in-flight or upcoming), from the
 *  raw assignment table — independent of the capacity engine's demand
 *  math. `in_flight` = the project is actively consuming capacity now. */
export interface PersonAssignmentItem {
  project_id: string;
  project_name: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  hours_per_week: number | null;
  in_flight: boolean;
}
export interface PersonAssignments {
  name: string;
  assignments: PersonAssignmentItem[];
}

/** Per-person live-or-upcoming assignments (completed/declined excluded).
 *  Powers the team page's "what is each person on + when do they free up"
 *  view. Ground truth from project_assignments, NOT engine demand. */
export function useRosterAssignments() {
  return useQuery<PersonAssignments[]>({
    queryKey: ["roster", "assignments"],
    queryFn: () => api.get("/roster/assignments").then((r) => r.data),
  });
}

export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RosterMemberPayload) => {
      const { data } = await api.post<TeamMember>("/roster/", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roster"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["capacity-rebuild"] });
    },
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      originalName,
      payload,
    }: {
      originalName: string;
      payload: RosterMemberPayload;
    }) => {
      const { data } = await api.put<TeamMember>(
        `/roster/${encodeURIComponent(originalName)}`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roster"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["capacity-rebuild"] });
    },
  });
}

export function useDeleteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.delete(`/roster/${encodeURIComponent(name)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roster"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
      qc.invalidateQueries({ queryKey: ["capacity-rebuild"] });
    },
  });
}
